<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportSectionCard from '../ReportSectionCard.vue';
import {
  featurePresentation,
  formatTokens,
  percentOf,
} from './aiUsagePresentation';

const props = defineProps({
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const rankedItems = computed(() => {
  const sorted = [...props.items].sort(
    (first, second) =>
      Number(second.total_tokens || 0) - Number(first.total_tokens || 0)
  );
  const maxTokens = Math.max(
    ...sorted.map(item => Number(item.total_tokens || 0)),
    1
  );
  const totalTokens = sorted.reduce(
    (sum, item) => sum + Number(item.total_tokens || 0),
    0
  );

  return sorted.map(item => ({
    ...item,
    presentation: featurePresentation(item.feature_name, t),
    barWidth: Math.max(3, percentOf(item.total_tokens, maxTokens)),
    share: percentOf(item.total_tokens, totalTokens),
  }));
});
</script>

<template>
  <ReportSectionCard
    :title="t('AI_USAGE_REPORT.BY_FEATURE.TITLE')"
    :description="t('AI_USAGE_REPORT.BY_FEATURE.DESCRIPTION')"
    icon="i-hugeicons-ranking"
  >
    <div v-if="loading" class="grid gap-3" aria-busy="true">
      <span
        v-for="index in 4"
        :key="index"
        class="h-11 rounded-lg bg-n-alpha-2 motion-safe:animate-pulse"
      />
    </div>

    <div
      v-else-if="!rankedItems.length"
      class="flex flex-col items-center gap-2 py-8 text-center"
    >
      <span
        class="i-hugeicons-ai-brain-01 size-8 text-n-slate-9"
        aria-hidden="true"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('AI_USAGE_REPORT.BY_FEATURE.EMPTY') }}
      </p>
    </div>

    <ol v-else class="m-0 grid list-none gap-3.5 p-0">
      <li
        v-for="(item, index) in rankedItems"
        :key="item.feature_name || index"
        data-test-id="aiFeatureItem"
        class="flex min-w-0 items-center gap-3"
        :title="item.feature_name"
      >
        <span
          class="w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-n-slate-10"
        >
          {{ index + 1 }}
        </span>
        <span
          class="grid size-9 shrink-0 place-items-center rounded-lg bg-n-solid-2 text-n-slate-11"
          aria-hidden="true"
        >
          <span :class="item.presentation.icon" class="size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline justify-between gap-3">
            <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
              {{ item.presentation.label }}
            </p>
            <span
              class="shrink-0 text-sm font-semibold tabular-nums text-n-slate-12"
            >
              {{
                t('AI_USAGE_REPORT.BY_FEATURE.TOKENS', {
                  count: formatTokens(item.total_tokens),
                })
              }}
            </span>
          </div>
          <div class="mt-1.5 h-1.5 overflow-hidden rounded-full bg-n-slate-4">
            <div
              class="h-full rounded-full bg-n-brand"
              :style="{ width: `${item.barWidth}%` }"
            />
          </div>
          <div
            class="mt-1 flex justify-between gap-3 text-xs tabular-nums text-n-slate-11"
          >
            <span class="truncate">
              {{
                t(
                  'AI_USAGE_REPORT.BY_FEATURE.CALLS',
                  { count: formatTokens(item.calls_count) },
                  Number(item.calls_count || 0)
                )
              }}
            </span>
            <span class="shrink-0">
              {{
                t('AI_USAGE_REPORT.BY_FEATURE.SHARE', { percent: item.share })
              }}
            </span>
          </div>
        </div>
      </li>
    </ol>
  </ReportSectionCard>
</template>
